import DualPanelSelectorController from "./dual_panel_selector_controller"
import Sortable from "sortablejs"

/**
 * ModuleSelectorController - Sélection de modules de formation
 *
 * Hérite de DualPanelSelectorController avec:
 * - Drag & drop pour réordonner les modules sélectionnés
 * - Bouton de création de module inline
 * - Affichage du nombre de documents attachés
 * - Protection contre la suppression de modules avec signatures
 */
export default class extends DualPanelSelectorController {
  static targets = [
    ...DualPanelSelectorController.targets,
    "createButton",
    "modalFrame",
    "confirmationModal",
    "confirmationInput",
    "confirmationButton",
    "confirmationMessage",
    "confirmationModuleName"
  ]

  static values = {
    ...DualPanelSelectorController.values,
    createModalUrl: String,
    inputPrefix: { type: String, default: "promotion[module_promotions_attributes]" },
    superadmin: { type: Boolean, default: false },
    cannotRemoveMessage: { type: String, default: "Ce module contient des signatures et ne peut pas être retiré." },
    confirmDestroyMessage: { type: String, default: "Ce module contient %{count} signature(s) d'émargement. Cette suppression est irréversible." },
    confirmClearMessage: { type: String, default: "Voulez-vous vraiment retirer les %{count} module(s) sans signatures ?" },
    clearResultMessage: { type: String, default: "%{removed} module(s) retiré(s). %{protected} module(s) avec signatures conservé(s)." }
  }

  connect() {
    // Sauvegarder les modules persistés pour détecter les suppressions au submit
    this.initialPersistedItems = this.selectedItemsValue
      .filter(item => item.module_promotion_id)
      .map(item => ({ id: item.id, module_promotion_id: item.module_promotion_id }))

    this.confirmedDestroyIds = []
    this.pendingRemovalItem = null

    super.connect()
    this.initSortable()
  }

  disconnect() {
    this.sortable?.destroy()
  }

  // ==================== Protection signatures ====================

  removeFromSelection(itemId) {
    const item = this.selectedItemsValue.find(i => i.id === itemId)

    if (item?.has_signed_attendances) {
      if (!this.superadminValue) {
        this.showProtectionAlert()
        return
      }
      this.pendingRemovalItem = item
      this.showConfirmationModal(item)
      return
    }

    super.removeFromSelection(itemId)
  }

  addToSelection(itemId, itemElement) {
    // Si on ré-ajoute un module précédemment confirmé pour suppression, nettoyer
    const persisted = this.initialPersistedItems.find(i => i.id === itemId)
    if (persisted) {
      this.confirmedDestroyIds = this.confirmedDestroyIds
        .filter(id => id !== persisted.module_promotion_id)
    }

    super.addToSelection(itemId, itemElement)
  }

  clearAll() {
    const unprotectedItems = this.selectedItemsValue.filter(i => !i.has_signed_attendances)
    const protectedItems = this.selectedItemsValue.filter(i => i.has_signed_attendances)

    if (unprotectedItems.length === 0) return

    if (unprotectedItems.length > this.confirmClearThresholdValue) {
      if (!confirm(this.confirmClearMessageValue.replace("%{count}", unprotectedItems.length))) {
        return
      }
    }

    // Conserver les modules protégés, retirer les autres
    const protectedIds = protectedItems.map(i => i.id)
    this.selectedIdsValue = protectedIds
    this.selectedItemsValue = protectedItems

    if (this.hasSelectedListTarget) {
      this.selectedListTarget.innerHTML = protectedItems
        .map(item => this.renderSelectedItemHTML(item))
        .join("")
    }

    this.updateCounts()
    this.updateHiddenInputs()
    this.performSearch()

    if (protectedItems.length === 0) {
      this.renderEmptySelected()
    }

    this.announce(this.clearResultMessageValue.replace("%{removed}", unprotectedItems.length).replace("%{protected}", protectedItems.length))
  }

  showProtectionAlert() {
    this.announce(this.cannotRemoveMessageValue)

    const alert = document.createElement("div")
    alert.className = "fixed top-4 right-4 z-50 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2"
    alert.setAttribute("role", "alert")
    alert.textContent = this.cannotRemoveMessageValue
    document.body.appendChild(alert)
    setTimeout(() => alert.remove(), 4000)
  }

  // ==================== Modal de confirmation (superadmin) ====================

  showConfirmationModal(item) {
    if (!this.hasConfirmationModalTarget) return

    const count = item.signed_attendances_count || 0
    this.confirmationModuleNameTarget.textContent = item.name
    this.confirmationMessageTarget.textContent =
      this.confirmDestroyMessageValue.replace("%{count}", count)

    this.confirmationInputTarget.value = ""
    this.confirmationInputTarget.dataset.expectedValue = item.name
    this.confirmationButtonTarget.disabled = true

    this.confirmationModalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    this.confirmationInputTarget.focus()
  }

  validateConfirmationInput() {
    const input = this.confirmationInputTarget
    const expected = input.dataset.expectedValue
    this.confirmationButtonTarget.disabled = input.value !== expected
  }

  confirmDestruction() {
    if (!this.pendingRemovalItem) return

    const item = this.pendingRemovalItem
    this.pendingRemovalItem = null

    if (item.module_promotion_id) {
      this.confirmedDestroyIds.push(item.module_promotion_id)
    }

    this.closeConfirmationModal()
    super.removeFromSelection(item.id)
  }

  cancelDestruction() {
    this.pendingRemovalItem = null
    this.closeConfirmationModal()
  }

  closeConfirmationModal() {
    if (this.hasConfirmationModalTarget) {
      this.confirmationModalTarget.classList.add("hidden")
      document.body.classList.remove("overflow-hidden")
    }
  }

  // ==================== Sortable ====================

  initSortable() {
    if (!this.hasSelectedListTarget) return

    this.sortable = Sortable.create(this.selectedListTarget, {
      animation: 150,
      handle: ".handle",
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      onEnd: () => this.updatePositions()
    })
  }

  updatePositions() {
    this.selectedItemsValue = Array.from(this.selectedListTarget.querySelectorAll("[data-item-id]"))
      .map((el, index) => {
        const id = parseInt(el.dataset.itemId)
        const item = this.selectedItemsValue.find(i => i.id === id)
        return { ...item, position: index + 1 }
      })

    this.updateHiddenInputs()
    this.announce("Ordre des modules mis à jour")
  }

  // ==================== API Response ====================

  getItemsFromResponse(data) {
    return data.modules || []
  }

  // ==================== Rendu disponibles ====================

  renderAvailableItemHTML(item) {
    const attachmentsBadge = item.attachments_count > 0
      ? `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
           <i class="fa-solid fa-paperclip mr-1" aria-hidden="true"></i>${item.attachments_count}
         </span>`
      : ""

    return `
      <button type="button"
              class="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 flex items-start gap-3"
              data-item-id="${item.id}"
              data-item-name="${this.escapeHtml(item.name)}"
              data-item-description="${this.escapeHtml(item.description || "")}"
              data-item-attachments="${item.attachments_count || 0}"
              data-action="click->module-selector#toggle"
              role="option"
              aria-selected="false"
              tabindex="0">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="font-medium text-gray-900 truncate">${this.escapeHtml(item.name)}</span>
            ${attachmentsBadge}
          </div>
          ${item.description ? `<p class="text-sm text-gray-500 mt-1 line-clamp-2">${this.escapeHtml(item.description)}</p>` : ""}
        </div>
        <i class="fa-solid fa-plus text-gray-400 group-hover:text-blue-500 flex-shrink-0 mt-1" aria-hidden="true"></i>
      </button>
    `
  }

  // ==================== Rendu sélectionnés ====================

  renderSelectedItemHTML(item) {
    const attachmentsBadge = item.attachments_count > 0
      ? `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
           <i class="fa-solid fa-paperclip mr-1" aria-hidden="true"></i>${item.attachments_count}
         </span>`
      : ""

    const protectedBadge = item.has_signed_attendances
      ? `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
           <i class="fa-solid fa-signature mr-1" aria-hidden="true"></i>${item.signed_attendances_count || 0}
         </span>`
      : ""

    return `
      <div class="flex items-center gap-3 p-3 bg-white rounded-lg border ${item.has_signed_attendances ? "border-amber-200" : "border-gray-200"} shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
           data-item-id="${item.id}"
           data-sortable-id="${item.id}"
           role="option"
           aria-selected="true"
           tabindex="0">
        <i class="fa-solid fa-grip-lines handle cursor-move text-gray-400 hover:text-gray-600" aria-hidden="true"></i>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="font-medium text-gray-900 truncate">${this.escapeHtml(item.name)}</span>
            ${attachmentsBadge}
            ${protectedBadge}
          </div>
        </div>
        <button type="button"
                class="text-red-400 hover:text-red-600 transition-colors p-1"
                data-action="click->module-selector#toggle"
                aria-label="Retirer ${this.escapeHtml(item.name)}"
                tabindex="-1">
          <i class="fa-solid fa-times" aria-hidden="true"></i>
        </button>
      </div>
    `
  }

  // ==================== Extraction données ====================

  extractItemData(element) {
    return {
      id: parseInt(element.dataset.itemId),
      name: element.dataset.itemName || "",
      description: element.dataset.itemDescription || "",
      attachments_count: parseInt(element.dataset.itemAttachments) || 0
    }
  }

  // ==================== Hidden inputs ====================

  // Suppression gérée par le controller directement (pas via _destroy nested attributes)
  // car le planning tab génère des entrées concurrentes qui annulent la destruction.
  //
  // IMPORTANT: Les clés doivent être numériques pures (ex: "100", "101") car Rails
  // strong parameters exige /\A\d+\z/ pour reconnaître un hash de nested attributes.
  // Les clés préfixées (ex: "sel_0") sont silencieusement rejetées par permit().
  // L'offset 100 évite les collisions avec l'onglet Planning qui utilise 0, 1, 2...
  updateHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return

    const prefix = this.inputPrefixValue
    const KEY_OFFSET = 100

    // Inputs pour les modules sélectionnés (existants + nouveaux)
    const parts = this.selectedItemsValue
      .map((item, index) => this.renderHiddenInputsForItem(item, KEY_OFFSET + index))

    // IDs des modules persistés à supprimer (param séparé, hors nested attributes)
    const currentIds = new Set(this.selectedItemsValue.map(i => i.id))
    const removedItems = this.initialPersistedItems.filter(i => !currentIds.has(i.id))

    removedItems.forEach(item => {
      parts.push(`<input type="hidden" name="remove_module_promotion_ids[]" value="${item.module_promotion_id}">`)
    })

    // Inputs de confirmation pour les suppressions validées par superadmin
    this.confirmedDestroyIds.forEach(id => {
      parts.push(`<input type="hidden" name="confirm_destroy_module_ids[]" value="${id}">`)
    })

    this.hiddenInputsTarget.innerHTML = parts.join("")

    // Désactiver les inputs de l'onglet Planning pour les modules supprimés
    this.syncPlanningTabInputs(removedItems)
  }

  syncPlanningTabInputs(removedItems) {
    const removedMpIds = new Set(removedItems.map(i => i.module_promotion_id))

    document.querySelectorAll("[data-planning-module-promotion-id]").forEach(wrapper => {
      const mpId = parseInt(wrapper.dataset.planningModulePromotionId)
      const inputs = wrapper.querySelectorAll("input[type=hidden]")

      if (removedMpIds.has(mpId)) {
        inputs.forEach(input => input.disabled = true)
      } else {
        inputs.forEach(input => input.disabled = false)
      }
    })
  }

  renderHiddenInputsForItem(item, key) {
    const prefix = this.inputPrefixValue

    const idInput = item.module_promotion_id
      ? `<input type="hidden" name="${prefix}[${key}][id]" value="${item.module_promotion_id}">`
      : ""

    // Pour les modules existants, envoyer la position courante.
    // Pour les nouveaux modules (sans module_promotion_id), ne pas envoyer de position
    // et laisser le gem positioned l'assigner automatiquement au prochain rang.
    const positionInput = item.module_promotion_id
      ? `<input type="hidden" name="${prefix}[${key}][position]" value="${item.position || 1}">`
      : ""

    return `
      ${idInput}
      <input type="hidden" name="${prefix}[${key}][learning_module_id]" value="${item.id}">
      ${positionInput}
    `
  }

  // ==================== Création inline ====================

  openCreateModal(event) {
    event.preventDefault()

    if (!this.createModalUrlValue) return

    // Charger la modale via Turbo Frame
    if (this.hasModalFrameTarget) {
      this.modalFrameTarget.src = this.createModalUrlValue
    }
  }

  // Appelé après création réussie via Turbo Stream
  handleModuleCreated(event) {
    const { id, name, description, attachments_count } = event.detail

    // Ajouter directement aux sélectionnés
    const newItem = { id, name, description, attachments_count: attachments_count || 0 }
    this.selectedIdsValue = [...this.selectedIdsValue, id]
    this.selectedItemsValue = [...this.selectedItemsValue, { ...newItem, position: this.selectedItemsValue.length + 1 }]

    this.renderSelectedItem(newItem)
    this.updateCounts()
    this.updateHiddenInputs()

    this.announce(`Module "${name}" créé et ajouté`)
  }
}
